import {
  getAttemptsFormKeys,
  getAttemptsFormValues
} from 'features/bulk-email/sequences/BulkSequences.utils';
import Handlebars from 'handlebars';
import strings from 'strings';
import { BULK_RESTRICTION_IDS, BULK_RESTRICTION_RULE, BulkScopes } from 'UI/constants/defaults';
import { getFeatureFlags, objectToArray } from 'UI/utils';

import { BulkDrawerFormFieldsMap } from '../BulkEmailForm/fields';

import { BULK_DRAWER_ERRORS_KEYS, REQUIRED_BULK_FIELD_KEYS } from './BulkEmailDrawer.constants';

const { bodyMessages } = strings.bulkEmails.dialogs.restrictions;

const { validations: validationsCopies } = strings.bulkEmails.forms.smartTags;
const { customSmartTagsValidations } = strings.bulkEmails.forms.errors;
const RECIPIENT_TYPE = 'recipient';

export const mapRestrictionId = restrictionId => {
  switch (restrictionId) {
    case BULK_RESTRICTION_IDS.scheduleCloseNearScheduled:
      return BULK_RESTRICTION_RULE.scheduleCloseNearScheduled;
    case BULK_RESTRICTION_IDS.scheduleCloseNearSent:
      return BULK_RESTRICTION_RULE.scheduleCloseNearSent;
    case BULK_RESTRICTION_IDS.sendNearSent:
      return BULK_RESTRICTION_RULE.sendNearSent;
    case BULK_RESTRICTION_IDS.sendNearScheduled:
      return BULK_RESTRICTION_RULE.sendNearScheduled;
    case BULK_RESTRICTION_IDS.noQuotaAvailable:
      return BULK_RESTRICTION_RULE.noQuotaAvailable;
    default:
      return BULK_RESTRICTION_RULE.defaultRestriction;
  }
};

/**
 * @typedef RuleReasonType
 * @property {string} rule
 * @property {string} reason
 */

/**
 * @typedef QuotaOptionsType
 * @property {number} remainingQuota
 * @property {number} searchProjectSize
 * @property {number} shouldSchedule
 */

/**
 * @param {QuotaOptionsType} quotaOptions
 * @returns {RuleReasonType}
 */
export const getQuotaRestriction = (quotaOptions = {}) => {
  const { remainingQuota, searchProjectSize, shouldSchedule } = quotaOptions;
  if (remainingQuota < 0 || typeof remainingQuota !== 'number') return null;

  if (remainingQuota === 0) {
    return {
      rule: BULK_RESTRICTION_RULE.noQuotaAvailable,
      reason: strings.formatString(bodyMessages.noQuotaAvailable, {
        saveAsDraft: shouldSchedule ? '' : bodyMessages.saveAsDraft
      })
    };
  }

  return remainingQuota < searchProjectSize
    ? {
        rule: BULK_RESTRICTION_RULE.quotaNearlyReached,
        reason: strings.formatString(bodyMessages.quotaNearlyReached, {
          remainingQuota,
          saveAsDraft: shouldSchedule ? '' : bodyMessages.saveAsDraft,
          searchProjectSize
        })
      }
    : null;
};

/**
 * Exported only for test purposes
 */

/**
 * @param {string} content
 * @param {Array} smartTags
 */
export const ensureTemplateIsValid = (content, smartTags = []) => {
  try {
    const data = {};
    const template = Handlebars.compile(content, { noEscape: true, strict: true });

    smartTags.length > 0 &&
      smartTags.forEach(tag => {
        const tagName = tag.value.match(/[a-z_]+/);
        data[tagName[0]] = '';
      });
    template(data);

    return {
      success: true
    };
  } catch (error) {
    const incorrectSmartTag = error.message.match(/"\w+"/gi);
    return {
      success: false,
      error: incorrectSmartTag && incorrectSmartTag[0]
    };
  }
};

const formatErrorCopy = (copy, values) => strings.formatString(copy, values);

export const checkWellWrittenSmartTags = (field, smartTags) => content => {
  const templateIsValid = ensureTemplateIsValid(content, smartTags);

  if (!templateIsValid.success) {
    return templateIsValid.error
      ? formatErrorCopy(validationsCopies.unsupportedSmarTag, {
          field,
          smartTag: templateIsValid.error
        })
      : formatErrorCopy(validationsCopies.wellWritten, { field });
  }

  return templateIsValid.success;
};

/**
 * Exported only for test purposes
 */
export const checkAtLeastOneSmartTag =
  ({ featureFlag, smartTags }) =>
  content => {
    if (!getFeatureFlags().includes(featureFlag) || !smartTags) return true;

    const recipientSmartTags = smartTags.filter(({ type }) => type === RECIPIENT_TYPE);
    const includesRecipientSmartTag = recipientSmartTags.some(
      ({ value: smartTag }) => !!content.match(smartTag)
    );

    return includesRecipientSmartTag || validationsCopies.atLeastOne;
  };
/**
 * @typedef CustomValidationsOptions
 * @property {String} fieldName
 * @property {String} featureFlag
 * @property {Array} smartTags
 */

/**
 * @param {CustomValidationsOptions} options
 * @returns {Object}
 */
export const getBulkEmailCustomValidations = (options = {}) => {
  const { fieldName, featureFlag, smartTags } = options;

  if (!fieldName || !featureFlag || !smartTags) throw new Error(customSmartTagsValidations);

  const validations = {
    validate: {
      [BULK_DRAWER_ERRORS_KEYS.wellWrittenSmartTags]: checkWellWrittenSmartTags(
        fieldName,
        smartTags
      ),
      [BULK_DRAWER_ERRORS_KEYS.atLeastOneSmartTag]: checkAtLeastOneSmartTag({
        featureFlag,
        smartTags
      })
    }
  };

  return validations;
};

export const isSmartTagsErrorActive = errors =>
  objectToArray(BULK_DRAWER_ERRORS_KEYS).some(
    error => errors?.subject?.type === error || errors?.emailBody?.type === error
  );

const validateMainRequiredFieldsAreFilled = watch => {
  const watchers = watch(REQUIRED_BULK_FIELD_KEYS);

  return REQUIRED_BULK_FIELD_KEYS.every(key => watchers[key]);
};

const validateRequiredFieldsByScopeAreFilled = watch => {
  const watchers = watch([
    BulkDrawerFormFieldsMap.BulkScope.key,
    BulkDrawerFormFieldsMap.GeneralBulkCategory.key,
    BulkDrawerFormFieldsMap.MarketCandidates.key,
    BulkDrawerFormFieldsMap.JobOrderRecruitment.key
  ]);
  const bulkScopeId = watchers[BulkDrawerFormFieldsMap.BulkScope.key]?.id;

  const requiredFieldsByScope = {
    [BulkScopes.General]: watchers[BulkDrawerFormFieldsMap.GeneralBulkCategory.key],
    [BulkScopes.Marketing]: watchers[BulkDrawerFormFieldsMap.MarketCandidates.key],
    [BulkScopes.Recruiting]: watchers[BulkDrawerFormFieldsMap.JobOrderRecruitment.key]
  };

  return requiredFieldsByScope[bulkScopeId];
};

const validateErrorsAreEmpty = errors => Object.keys(errors).length === 0;

export const validateStepsFieldsFilledCorrectly = ({ errors, watch }) => {
  const ids = watch(BulkDrawerFormFieldsMap.Attempts.key, []);
  const keys = getAttemptsFormKeys(ids);

  return (
    keys.every(
      key => watch(`${key}.subject`) && watch(`${key}.emailBody`) && watch(`${key}.triggerDays`)
    ) && validateErrorsAreEmpty(errors)
  );
};

export const validateBulkSequenceIsFilledCorrectly = ({ errors, watch }) => {
  return (
    validateMainRequiredFieldsAreFilled(watch) &&
    validateRequiredFieldsByScopeAreFilled(watch) &&
    validateStepsFieldsFilledCorrectly({ errors, watch })
  );
};

export const validateHasInvalidAttempts = watch => {
  const attemptsWatchers = getAttemptsFormValues(watch);

  return Object.entries(attemptsWatchers).some(
    ([, value]) => !value || !value?.subject || !value.emailBody
  );
};
